import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import type { ReactNode } from 'react';

import type { ChatContentProps } from './chat-conversation-content';
import { ChatSettingsPanel } from './chat-settings-panel';
import { CustomSkillEditScreen } from './custom-skill-edit-screen';
import { CreateSkillView } from './custom-skills-ui';
import { NovaChatConversation } from './nova-chat-conversation';
import { NovaScheduleDetailView, NovaSchedulesSettings } from './nova-schedules-settings';
import { OutlookManageView } from './outlook-connect-settings';
import { useSettingsPanelContext } from './settings-panel-context';
import { TeamCreateView } from './skill-administration/team-create-view';
import { NOVA_SCHEDULES_FEATURE_FLAG } from '../../../lib/nova-schedule-proposal';
import type { NovaView } from '../navigation/types';

/** Screen selection for memory-navigation surfaces such as embeds and add-ins. */
export function NovaScreen({
  view,
  children,
  settingsPanel,
  ...chatProps
}: ChatContentProps & {
  view: NovaView;
  children?: ReactNode;
  settingsPanel?: ReactNode;
}) {
  const { popView, backHandlerRef } = useSettingsPanelContext();
  const schedulesEnabled = useFeatureFlag(NOVA_SCHEDULES_FEATURE_FLAG, false);
  const skillsEnabled = useFeatureFlag('nova-skills', false);
  const managementEnabled = useFeatureFlag('nova-skills-management', false);
  if (view.kind === 'scheduled' && schedulesEnabled) {
    if (view.scheduleUuid)
      return <NovaScheduleDetailView key={view.scheduleUuid} scheduleUuid={view.scheduleUuid} onBack={popView} />;
    return (
      <div className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto px-8 pt-10 pb-8 [scrollbar-gutter:stable_both-edges]">
        <div className="flex w-[736px] max-w-full shrink-0 flex-col gap-4">
          <NovaSchedulesSettings />
        </div>
      </div>
    );
  }
  if (view.kind === 'settings') {
    const subView = view.subView;
    let content: ReactNode;
    if (subView?.type === 'outlook-manage') content = <OutlookManageView onBack={popView} />;
    else if (subView?.type === 'team-create')
      content = <TeamCreateView userUuid={subView.userUuid} onBack={popView} cancelRef={backHandlerRef} />;
    else if (skillsEnabled && managementEnabled) {
      if (subView?.type === 'custom-skill-create')
        content = <CreateSkillView onBack={popView} cancelRef={backHandlerRef} />;
      if (subView?.type === 'custom-skill-edit') content = <CustomSkillEditScreen skillUuid={subView.skillUuid} />;
    }
    if (content) return <div className="flex min-h-0 flex-1 flex-col">{content}</div>;
    return settingsPanel ?? <ChatSettingsPanel section={view.section} />;
  }
  return children ?? <NovaChatConversation {...chatProps} />;
}
